import React from 'react';
import { X, Droplets } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { Section, Field } from './education/Section';

export interface WaterWellProperties {
  id: number;
  tracking_number: string | null;
  county: string | null;
  proposed_use: string | null;
  total_depth_ft: number | null;
  yield_gpm: number | null;
  drawdown_ft: number | null;
  drill_date: string | null;
  driller_name: string | null;
}

interface Props {
  well: WaterWellProperties;
  onClose: () => void;
}

// 1 acre-foot = 325,851 gallons → 1 AFY ≈ 893 gal/day
const GAL_PER_AFY = 325_851;
const BTM_SITE_AFY = 1;

function formatDate(raw: string | null): string | null {
  if (!raw) return null;
  const m = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : String(raw);
}

export const WaterWellDetail: React.FC<Props> = ({ well, onClose }) => {
  const { theme } = useTheme();
  const color = '#0ea5e9';

  const yieldGpm = well.yield_gpm != null ? Number(well.yield_gpm) : null;
  const depthFt = well.total_depth_ft != null ? Number(well.total_depth_ft) : null;
  const drawdownFt = well.drawdown_ft != null ? Number(well.drawdown_ft) : null;
  const drilled = formatDate(well.drill_date);
  const age = drilled ? new Date().getFullYear() - parseInt(drilled.slice(0, 4), 10) : null;

  // Yield → supply math: assume 24/7 sustained pumping (theoretical max)
  const gpdAt24x7 = yieldGpm != null ? yieldGpm * 60 * 24 : null;
  const afyEquivalent = gpdAt24x7 != null ? (gpdAt24x7 * 365) / GAL_PER_AFY : null;
  const sitesSupported = afyEquivalent != null ? Math.floor(afyEquivalent / BTM_SITE_AFY) : null;

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Droplets size={18} color={color} />
          <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: theme.text }}>
            Water Well
          </h2>
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '2px',
          color: theme.textSecondary,
        }}>
          <X size={18} />
        </button>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {/* Yield hero */}
        <div style={{
          padding: '14px', marginBottom: '16px', borderRadius: '8px',
          background: color + '15', border: `1px solid ${color}40`,
        }}>
          <div style={{ fontSize: '28px', fontWeight: 700, color, lineHeight: 1.1 }}>
            {yieldGpm != null ? `${yieldGpm.toLocaleString()} gpm` : 'Yield not reported'}
          </div>
          {well.proposed_use && (
            <div style={{ fontSize: '13px', fontWeight: 600, color, marginTop: '4px', textTransform: 'capitalize' }}>
              {well.proposed_use}
            </div>
          )}
          {well.tracking_number && (
            <div style={{ fontSize: '11px', color: theme.textSecondary, marginTop: '4px', fontFamily: 'monospace' }}>
              TWDB #{well.tracking_number}
            </div>
          )}
        </div>

        {/* Well construction */}
        <Section title="Construction">
          {depthFt != null && <Field label="Total Depth" value={`${depthFt.toLocaleString()} ft`} />}
          {yieldGpm != null && <Field label="Tested Yield" value={`${yieldGpm.toLocaleString()} gpm`} />}
          {drawdownFt != null && <Field label="Drawdown" value={`${drawdownFt.toLocaleString()} ft`} />}
          {drilled && (
            <Field
              label="Drilled"
              value={age != null && age > 0 ? `${drilled} (${age}y ago)` : drilled}
            />
          )}
          {well.driller_name && <Field label="Driller" value={well.driller_name} />}
          {well.county && <Field label="County" value={well.county} />}
        </Section>

        {/* BTM supply capacity */}
        {yieldGpm != null && afyEquivalent != null && (
          <Section title="BTM Supply Capacity">
            <div style={{
              padding: '10px', borderRadius: '6px', marginBottom: '8px',
              background: 'rgba(14,165,233,0.1)', border: '1px solid rgba(14,165,233,0.25)',
            }}>
              <div style={{ fontSize: '12px', fontWeight: 700, marginBottom: '6px', color }}>
                Capacity translation
              </div>
              <div style={{ fontSize: '12px', color: theme.text, lineHeight: 1.7 }}>
                <div>
                  <strong>{(gpdAt24x7 ?? 0).toLocaleString()} gpd</strong> at 24/7 sustained pumping
                </div>
                <div>
                  ≈ <strong>{Math.round(afyEquivalent).toLocaleString()} AFY</strong> theoretical max
                </div>
                {sitesSupported != null && sitesSupported > 0 && (
                  <div style={{ marginTop: '6px', fontWeight: 600, color: '#0f766e' }}>
                    {sitesSupported >= 100
                      ? `≫ enough for hundreds of BTM sites at ${BTM_SITE_AFY} AFY each`
                      : sitesSupported >= 10
                      ? `Enough for ~${sitesSupported} BTM sites at ${BTM_SITE_AFY} AFY each`
                      : sitesSupported >= 1
                      ? `Enough for ${sitesSupported} BTM site${sitesSupported === 1 ? '' : 's'} at ${BTM_SITE_AFY} AFY each`
                      : `Below a single-site BTM target (< ${BTM_SITE_AFY} AFY at 24/7)`}
                  </div>
                )}
              </div>
            </div>
            <div style={{ fontSize: '11px', color: theme.textSecondary, lineHeight: 1.6 }}>
              1 AFY ≈ 893 gpd. Numbers above assume continuous pumping at the reported tested yield;
              real production depends on duty cycle, drawdown sustainability, and GCD permit limits.
            </div>
          </Section>
        )}

        {/* Data age / context */}
        {age != null && age > 30 && (
          <Section title="Data Age">
            <div style={{ fontSize: '11px', color: theme.textSecondary, lineHeight: 1.6 }}>
              This well is {age} years old. Older wells may have degraded casing, reduced yield, or be
              abandoned. Use the formation evidence (depth, lithology) but plan to drill a new well
              during site DD.
            </div>
          </Section>
        )}

        {/* Water story nudge */}
        <Section title="Water Story Context">
          <div style={{ fontSize: '11px', color: theme.textSecondary, lineHeight: 1.6 }}>
            Toggle <strong>Brackish Aquifer Extent</strong> and <strong>Water Quality (TDS)</strong> in
            the Water sidebar section to see whether this well sits in a brackish zone and what TDS values
            nearby wells have reported. Yield + quality together tell you whether this is a viable BTM
            supply strategy.
          </div>
        </Section>

        {/* Source */}
        <Section title="Data Source">
          <div style={{ fontSize: '11px', color: theme.textTertiary, lineHeight: 1.6 }}>
            TWDB Submitted Drillers Reports (SDR) — Texas Water Development Board.
          </div>
        </Section>
      </div>
    </div>
  );
};
